<div class="dash-shell">
  <header class="page-head">
    <div class="page-head__top">
      <nav class="page-head__crumbs" aria-label="Breadcrumb">
        <a class="page-head__crumb" routerLink="/ops/dashboard">Ops</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
        <span class="page-head__crumb">Debtors Finance</span>
        <span class="page-head__sep" aria-hidden="true">/</span>
        <a class="page-head__crumb" routerLink="/ops/df/facilities">Facilities</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
        <span class="page-head__crumb page-head__crumb--current" aria-current="page">
          {{ facility()?.entity_name ?? 'Facility' }}
        </span>
      </nav>
      @if (facility(); as f) {
        <app-assignment-panel kind="DF_FACILITY" [shortId]="f.short_id" />
      }
    </div>

    @if (facility(); as f) {
      <div class="page-head__bar">
        <div class="page-head__titles">
          <h1 class="page-head__title">{{ f.entity_name }}</h1>
          <p class="page-head__subtitle">
            @if (f.requested_limit) {
              Requested: {{ f.requested_limit | zar }}
            }
            @if (daysSinceSubmission() !== null) {
              <span class="page-head__age" aria-label="Time since submission">
                ·
                {{
                  daysSinceSubmission() === 0
                    ? 'submitted today'
                    : 'submitted ' +
                      daysSinceSubmission() +
                      (daysSinceSubmission() === 1 ? ' day ago' : ' days ago')
                }}
              </span>
            }
          </p>
        </div>
        <dl class="page-head__statuses">
          <div class="page-head__status">
            <dt>Facility</dt>
            <dd><app-status-chip [status]="f.df_status" /></dd>
          </div>
          <div class="page-head__status">
            <dt>Application</dt>
            <dd><app-status-chip [status]="f.application_stage" /></dd>
          </div>
          @if (f.facility_subtype) {
            <div class="page-head__status">
              <dt>Type</dt>
              <dd><app-status-chip [status]="f.facility_subtype" /></dd>
            </div>
          }
        </dl>
      </div>
    }
  </header>

  @if (isLoading()) {
    <mat-card appearance="outlined" aria-label="Loading facility">
      <mat-card-content class="skeleton-card">
        <span class="sg-skeleton sg-skeleton--heading" style="width: 45%"></span>
        <span class="sg-skeleton sg-skeleton--text" style="width: 30%"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
        <span class="sg-skeleton sg-skeleton--text sg-skeleton--w50"></span>
      </mat-card-content>
    </mat-card>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--bordered sg-empty--error">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this facility</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <div class="sg-empty__actions">
        <button mat-stroked-button type="button" (click)="load()">
          <app-icon name="refresh" [size]="18" /> Try again
        </button>
        <a mat-stroked-button routerLink="/ops/df/facilities">Back to queue</a>
      </div>
    </div>
  } @else if (facility(); as f) {
    @if (serverError(); as error) {
      <div class="error-banner" role="alert">{{ error }}</div>
    }

    <!-- DOC-52 Screen 9.6 placement — "the facility header / decision context".
         This page also carries Screens 9.8 (assessment) and 9.9 (limit
         decision), so the flag renders a second time at the Decision card;
         DOC-52 §2 wants it beside the controls, not merely on the page. -->
    <app-ops-recovery-flag [flag]="f.ks_recovery_flag" />

    <!-- KS→DF conversion journey -->
    @if (f.converted_from; as ks) {
      <mat-card appearance="outlined" class="conversion-card">
        <mat-card-content>
          <div class="conversion-card__row">
            <app-icon name="arrow-right" [size]="18" />
            <span>
              Converted from Kickstart Cash
              <a routerLink="/ops/kickstart/{{ ks.short_id }}">{{ ks.short_id }}</a>
              ({{ ks.amount_requested | zar }})
            </span>
            <app-status-chip [status]="ks.ks_status" />
          </div>
        </mat-card-content>
      </mat-card>
    }

    <!-- ── Assess: what the AI says, then the decision ──────────────────────── -->

    <app-ai-advisory-panel
      [advisory]="f.latest_ai_advisory"
      [running]="isAssessing()"
      [committeeCleared]="committeeCleared()"
      (assess)="rerunAssessment()"
    />

    <app-ai-notes
      section="DEBTORS_FINANCE"
      [sourceRef]="f.short_id"
      [calls]="aiCost()?.calls ?? []"
    />

    <!-- What this facility has cost in AI spend (Ops-only). -->
    <app-ai-cost-panel [breakdown]="aiCost()" [loading]="aiCostLoading()" />

    <app-ai-comparison-panel product="df" [targetId]="f.short_id" />

    <!-- Decision actions -->
    <mat-card appearance="outlined" class="actions-card">
      <mat-card-content>
        <h2 class="section-title">Decision</h2>

        <!-- DOC-52 Screen 9.9 placement — at the limit-decision controls. It is
             a decision *input* and nothing else: it disables no control below,
             adds no confirmation step, and never restricts this facility (N6). -->
        <app-ops-recovery-flag [flag]="f.ks_recovery_flag" />

        <!-- FLOW-283 — the declared-account hold (FLOW-277), above the controls it
             actually blocks. On this lane there is no automated sizing to stop, so
             what waits is THE OFFER: approve refuses while this is open. 🚨 `DOC-58`
             UI-DA-58-02 PRE-SIZING — no facility size is shown, placed or implied
             here; `facility_limit` is set by the approve this card is waiting on. -->
        @if (f.sizing_hold?.is_open) {
          <app-sizing-hold-card
            [hold]="f.sizing_hold"
            [affirmationInForce]="f.affirmation_in_force"
            [saveRelease]="releaseHold"
          />
        }

        @if (committeeCleared()) {
          <div class="cleared-banner" role="status">
            <app-icon name="check-circle" [size]="16" />
            <span>
              Credit Committee clearance confirmed (<code>{{ passedCcRef() }}</code
              >). The reference is filled in below.
            </span>
          </div>
        } @else if (needsCc()) {
          <div class="warning-banner" role="alert">
            Requested limit exceeds ZAR 1,000,000 — a Credit Committee resolution is required before
            approval. Open one in the Credit Committee section below, then enter its reference here.
          </div>
        }

        <!-- Cession of the debtor book (BRE-200 DF-OB-07) — a precondition of
             approval, so it sits above it: until this is confirmed (or waived)
             activation is blocked and Approve will fail on the onboarding gate. -->
        <details class="action-panel" [open]="!f.cession_confirmed">
          <summary class="action-panel__title">
            <span class="action-panel__chevron"><app-icon name="chevron-right" [size]="16" /></span>
            <span>Cession of debtor book</span>
            <span class="action-panel__authority">Authorised Ops user</span>
          </summary>
          <div class="form">
            @if (f.cession_confirmed) {
              <p class="muted" data-testid="cession-confirmed">
                Cession confirmed {{ f.cession_confirmed_at | date: 'medium' | dash }}.
              </p>
            } @else {
              <p class="muted">
                Confirm cession of the debtor book to CapX, or a TPFA arrangement. Existing cessions
                to other funders must be cancelled or waived first — the system cannot verify this,
                so it rests on your check.
              </p>
              <div class="form__actions">
                <button
                  mat-stroked-button
                  type="button"
                  [disabled]="isActioning()"
                  data-testid="confirm-cession-btn"
                  (click)="confirmCession()"
                >
                  {{ isActioning() ? 'Confirming…' : 'Confirm cession' }}
                </button>
              </div>
            }
          </div>
        </details>

        <!-- Approve (expected path → primary-weighted) -->
        <details class="action-panel action-panel--primary" open>
          <summary class="action-panel__title">
            <span class="action-panel__chevron"><app-icon name="chevron-right" [size]="16" /></span>
            <span>Approve</span>
            <span class="action-panel__authority">
              {{ needsCc() ? 'Credit Committee reference required' : 'Authorised Ops user' }}
            </span>
          </summary>
          <form [formGroup]="approveForm" (ngSubmit)="approve()" class="form">
            <mat-form-field appearance="outline">
              <mat-label>Facility type</mat-label>
              <mat-select formControlName="facility_subtype">
                @for (st of subtypes; track st) {
                  <mat-option [value]="st">{{ subtypeLabels[st] }}</mat-option>
                }
              </mat-select>
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Approved facility limit (ZAR)</mat-label>
              <input matInput type="number" formControlName="facility_limit" min="1" />
              @if (
                approveForm.controls.facility_limit.touched &&
                approveForm.controls.facility_limit.invalid
              ) {
                <mat-error>Required</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>
                Credit Committee reference{{ needsCc() ? '' : ' (if limit > ZAR 1,000,000)' }}
              </mat-label>
              <input matInput formControlName="cc_ref" />
              @if (approveForm.controls.cc_ref.touched && approveForm.controls.cc_ref.invalid) {
                <mat-error>A Credit Committee reference is required above ZAR 1,000,000</mat-error>
              }
            </mat-form-field>
            <mat-form-field appearance="outline">
              <mat-label>Note (optional)</mat-label>
              <textarea matInput formControlName="note" rows="2"></textarea>
            </mat-form-field>
            <div class="form__actions">
              <button
                mat-flat-button
                color="primary"
                type="submit"
                [disabled]="isActioning()"
                data-testid="approve-btn"
              >
                @if (isActioning()) {
                  <mat-spinner diameter="20" />
                } @else {
                  <span>Approve</span>
                }
              </button>
            </div>
          </form>
        </details>

        <!-- Decline -->
        <details class="action-panel">
          <summary class="action-panel__title action-panel__title--destructive">
            <span class="action-panel__chevron"><app-icon name="chevron-right" [size]="16" /></span>
            <span>Decline</span>
            <span class="action-panel__authority">Authorised Ops user</span>
          </summary>
          <form [formGroup]="declineForm" (ngSubmit)="decline()" class="form">
            <mat-form-field appearance="outline">
              <mat-label>Reason for decline</mat-label>
              <textarea matInput formControlName="reason" rows="3"></textarea>
              @if (declineForm.controls.reason.touched && declineForm.controls.reason.invalid) {
                <mat-error>A reason is required</mat-error>
              }
            </mat-form-field>
            <div class="form__actions">
              <button
                mat-stroked-button
                type="submit"
                [disabled]="isActioning()"
                data-testid="decline-btn"
              >
                {{ isActioning() ? 'Declining…' : 'Decline' }}
              </button>
            </div>
          </form>
        </details>
      </mat-card-content>
    </mat-card>

    <!-- ── Evidence: the risk picture and supporting data ──────────────────── -->

    <!-- Risk summary (age-analysis lens) -->
    <app-risk-summary-panel [riskSummary]="f.risk_summary" />

    @if (debtorBookMaybeUncaptured()) {
      <div class="warning-banner" role="alert">
        No debtor book / age analysis has been captured for this facility yet. Verify the debtor
        book before deciding — a zero here may mean "not yet captured", not "no debtors".
      </div>
    }

    <!-- Credit Committee resolutions -->
    <!-- Entity-level verification (BRE-200 §2.4) — the canonical record,
         shared with every Kickstart application this business holds. -->
    <app-entity-verification-panel [entityShortId]="f.entity_short_id" />

    <!-- The declared lists (FLOW-243) — funders, related parties, directors.
         Ops may ADD what a statement reveals, and the row records that CapX
         added it: an Ops entry is not the client's declaration, and the two
         must never be read as the same act. -->
    <app-declarations-panel [entityShortId]="f.entity_short_id" mode="ops" />

    <app-cc-resolution-panel
      subjectKind="DF_FACILITY"
      [subjectId]="f.id"
      (passedRefChange)="onPassedRef($event)"
    />

    <!-- Facility details -->
    <mat-card appearance="outlined">
      <mat-card-content>
        <h2 class="section-title">Facility details</h2>
        <dl class="facts">
          <div class="facts__row" [class.facts__row--empty]="!f.df_intent">
            <dt>Intent</dt>
            <dd>{{ f.df_intent | dash }}</dd>
          </div>
          <div class="facts__row" [class.facts__row--empty]="!f.submitted_at">
            <dt>Submitted</dt>
            <dd>{{ f.submitted_at ? (f.submitted_at | date: 'medium') : '—' }}</dd>
          </div>
        </dl>
        <!--
          FLOW-290 — whether a fresh AI run may be asked for on this file. Sits
          in the facility facts — this screen has no documents panel, and the credit metrics next door are exactly where it must not go, deliberately NOT in the risk lens or the credit metrics: a
          withdrawn consent is a client exercising a right (POPIA s11(2)), not a
          finding about them.
        -->
        <app-ai-consent-ops-record [entityShortId]="f.entity_short_id" [state]="f.ai_consent" />
      </mat-card-content>
    </mat-card>

    <!-- Credit metrics (post-decision sizing — mostly populated after approval) -->
    <mat-card appearance="outlined">
      <mat-card-content>
        <h2 class="section-title">Credit metrics</h2>
        <dl class="facts">
          <div class="facts__row" [class.facts__row--empty]="!f.facility_limit">
            <dt>Facility limit</dt>
            <dd>{{ f.facility_limit | zar }}</dd>
          </div>
          <div class="facts__row">
            <dt>Insurance status</dt>
            <dd>
              <app-insurance-panel
                [status]="f.insurance_status"
                [history]="f.insurance_history"
                [triggers]="f.insurance_triggers"
                [save]="saveInsurance"
              />
            </dd>
          </div>
          <div class="facts__row" [class.facts__row--empty]="!f.dri_band">
            <dt>DRI band</dt>
            <dd>{{ f.dri_band | dash }}</dd>
          </div>
          <div class="facts__row" [class.facts__row--empty]="!f.concentration_lock_cause">
            <dt>Concentration lock cause</dt>
            <dd>{{ f.concentration_lock_cause | dash }}</dd>
          </div>
          <div class="facts__row" [class.facts__row--empty]="!f.decided_at">
            <dt>Decision date</dt>
            <dd>{{ f.decided_at ? (f.decided_at | date: 'medium') : '—' }}</dd>
          </div>
        </dl>
      </mat-card-content>
    </mat-card>

    <!-- Status timeline -->
    @if (f.status_events.length > 0) {
      <mat-card appearance="outlined">
        <mat-card-content>
          <h2 class="section-title">Status history</h2>
          <ol class="timeline">
            @for (event of f.status_events; track $index) {
              <li class="timeline__item">
                <app-status-chip [status]="event.to_status" />
                <span class="timeline__meta">
                  {{ event.created_at | date: 'medium' }}
                  @if (event.actor_name) {
                    · {{ event.actor_name }}
                  }
                </span>
                @if (event.note) {
                  <p class="timeline__note">{{ event.note }}</p>
                }
              </li>
            }
          </ol>
        </mat-card-content>
      </mat-card>
    }

    <!-- Review notes -->
    <mat-card appearance="outlined">
      <mat-card-content>
        <h2 class="section-title">Review notes</h2>
        @if (f.review_notes.length > 0) {
          <ol class="timeline">
            @for (note of f.review_notes; track note.short_id) {
              <li class="timeline__item">
                <p class="timeline__note">{{ note.body }}</p>
                <span class="timeline__meta">
                  {{ note.created_at | date: 'medium' }} · {{ note.author_name }}
                </span>
              </li>
            }
          </ol>
        } @else {
          <p class="muted">No review notes yet.</p>
        }
      </mat-card-content>
    </mat-card>

    <!-- Communication -->
    <mat-card appearance="outlined">
      <mat-card-content>
        <h2 class="section-title">Communication</h2>
        <app-email-trail [emails]="emails()" [loading]="emailsLoading()" />
      </mat-card-content>
    </mat-card>
  }
</div>
